import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { timeSheetValidationSchema } from "../../../../components/Helpers";
import ModalComponent from "../../../../components/Modal";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import CustomDropdown from "../../../../components/CustomDropdown";
import FormField from "../../../../components/FormField";
import FormButton from "../../../../components/FormButton";
import FormRow from "../../../../components/FormRow";
import MuiDatePicker from "../../../../components/MuiDatePicker";
import { toast } from "react-toastify";

import { FaTrash, FaPlus, FaDownload } from "react-icons/fa";
import { closeTimesheetModal, requestDownloadFileById, setResetDownloadFileById } from "../../../../Redux/TimeSheetState/TimeSheetActionCreator";

const EditTimesheet = ({
  onEditTimeSheet,
  timesheetValues,
  projectsList,
  isLoading,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [deletedFileNames, setDeletedFileNames] = useState([]);
  const modalState = useSelector((state) => state.TimesheetModalReducer);
  const downloadFileState = useSelector((state) => state.DownloadFileByIdReducer);
  const dispatch = useDispatch();
  
  // Handle download file state changes
  useEffect(() => {
    if (!downloadFileState?.loading && downloadFileState?.response) {
      // toast.success("File downloaded successfully");
      // Reset the download state
      dispatch(setResetDownloadFileById());
    } else if (!downloadFileState?.loading && downloadFileState?.error) {
      toast.error(downloadFileState?.error?.errorData?.data?.error || downloadFileState?.error?.errorData?.data || "something went wrong");
      // Reset the download state
      dispatch(setResetDownloadFileById());
    }
  }, [downloadFileState]);

  
  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
  
    try {
      const validHolidays = (values.holidays || []).filter(
        (holiday) => holiday.employee_holiday_date && holiday.notes
      );
  
      if (values.holidays.length > 0 && validHolidays.length !== values.holidays.length) {
        toast.error("Please fill in all holiday details or remove empty holidays");
        setSubmitting(false);
        return;
      }
  
      const timeSheetData = {
        ...values,
        projectId: values.projectId?.id || values.projectId,
        startDate: moment(values.startDate, "MM-DD-YYYY").format("MM-DD-YYYY"),
        endDate: moment(values.endDate, "MM-DD-YYYY").format("MM-DD-YYYY"),
        holidays: validHolidays.map((holiday) => ({
          ...(holiday.id && { id: holiday.id }),
          employee_holiday_date: moment(holiday.employee_holiday_date, "MM-DD-YYYY").format("MM-DD-YYYY"),
          notes: holiday.notes,
        })),
      };
  
      await onEditTimeSheet(timeSheetData, selectedFiles); // Await the promise
    } catch (error) {
      toast.error("Error in submitting timesheet");
    } finally {
      setSubmitting(false);
    }
  };
  

  // const handleSubmit = (values, { setSubmitting }) => {
  //   try {
  //     setSubmitting(true);
      
  //     // Validate holidays
  //     const validHolidays = values.holidays.filter(holiday => 
  //       holiday.employee_holiday_date && holiday.notes
  //     );

  //     // Only validate if there are holidays
  //     if (values.holidays.length > 0 && validHolidays.length !== values.holidays.length) {
  //       toast.error("Please fill in all holiday details or remove empty holidays");
  //       setSubmitting(false);
  //       return;
  //     }

  //     const timeSheetData = {
  //       ...values,
  //       projectId: values.projectId?.id || values.projectId,
  //       startDate: moment(values.startDate).format("MM-DD-YYYY"),
  //       endDate: moment(values.endDate).format("MM-DD-YYYY"),
  //       holidays: validHolidays.map(holiday => ({
  //         ...(holiday.id && { id: holiday.id }),
  //         employee_holiday_date: moment(holiday.employee_holiday_date).format("MM-DD-YYYY"),
  //         notes: holiday.notes
  //       }))
  //     };

  //     onEditTimeSheet(timeSheetData, selectedFiles);
      
  //     setTimeout(() => {
  //       setSubmitting(false);
  //     }, 1000);
  //   } catch (error) {
  //     setSubmitting(false);
  //     toast.error("Error submitting form: " + error.message);
  //   }
  // };

  const handleClose = () => {
    dispatch(closeTimesheetModal());
    setSelectedFiles([]);
    setDeletedFileNames([]); // Reset deleted files on close
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} size should be less than 10MB`);
        return false;
      }

      // Check for duplicates in both existing files and newly selected files
      // Allow if the file was previously deleted
      const isDuplicateInExisting = timesheetValues?.files?.some(
        existingFile => existingFile.fileName === file.name && !deletedFileNames.includes(file.name)
      );
      const isDuplicateInSelected = selectedFiles.some(
        selectedFile => selectedFile.name === file.name
      );

      if (isDuplicateInExisting || isDuplicateInSelected) {
        toast.error(`File "${file.name}" already exists.`);
        return false;
      }

      return true;
    });
  
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  
    // Clear the input to allow re-selection of same file if needed
    event.target.value = null;
  };
  
  const handleDrop = (event, setFieldValue) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  
    const files = Array.from(event.dataTransfer.files);
    const validFiles = files.filter((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} size should be less than 10MB`);
        return false;
      }

      // Check for duplicates in both existing files and newly selected files
      // Allow if the file was previously deleted
      const isDuplicateInExisting = timesheetValues?.files?.some(
        existingFile => existingFile.fileName === file.name && !deletedFileNames.includes(file.name)
      );
      const isDuplicateInSelected = selectedFiles.some(
        selectedFile => selectedFile.name === file.name
      );

      if (isDuplicateInExisting || isDuplicateInSelected) {
        toast.error(`File "${file.name}" already exists.`);
        return false;
      }

      return true;
    });
  
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setFieldValue('files', []);
    }
  };
  

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDeleteFile = (e, fileIndex, setFieldValue) => {
    e.preventDefault();
    e.stopPropagation();
    // Add the deleted file name to the array of deleted files
    const fileToDelete = selectedFiles[fileIndex];
    if (fileToDelete) {
      setDeletedFileNames(prev => [...prev, fileToDelete.name]);
    }
    setSelectedFiles(prev => prev.filter((_, index) => index !== fileIndex));
    setFieldValue('files', []);
  };

  const handleDeleteExistingFile = (e, fileId, setFieldValue, values) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Find the file being deleted and add its name to deletedFileNames
    const fileToDelete = values.files.find(file => file.id === fileId);
    if (fileToDelete) {
      setDeletedFileNames(prev => [...prev, fileToDelete.fileName]);
    }
    
    const updatedFiles = (values.files || []).filter(file => file.id !== fileId);
    setFieldValue('files', updatedFiles);
  };

  const handleDownloadFile = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
        
    dispatch(requestDownloadFileById({
      tId: timesheetValues?.id,
      fileId: file
    }));
  }

  return (
    <ModalComponent
      show={modalState.isOpen}
      onClose={handleClose}
      title={
        // <div className="flex items-center gap-2">
          <span className="text-indigo-500">Edit Timesheet</span>
        // </div>
      }
      className="w-full max-w-[95vw] sm:max-w-4xl mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
    >
      <Formik
        initialValues={{
          id: timesheetValues?.id,
          userId: timesheetValues?.userId,
          projectId: timesheetValues?.projectId,
          work_location: timesheetValues?.work_location,
          startDate: timesheetValues?.startDate ? moment(timesheetValues.startDate, "MM-DD-YYYY") : null,
          endDate: timesheetValues?.endDate ? moment(timesheetValues.endDate, "MM-DD-YYYY") : null,
          total_hours: timesheetValues?.total_hours,
          status: timesheetValues?.status,
          timesheet_type: timesheetValues?.timesheet_type,
          files: timesheetValues?.files,
          holidays: timesheetValues?.holidays?.map(holiday => ({
            id: holiday.id,
            employee_holiday_date: moment(holiday.employee_holiday_date, "MM-DD-YYYY"),
            notes: holiday.notes
          })) || []
        }}
        enableReinitialize
        validationSchema={timeSheetValidationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form className="p-2">
            <div className="flex flex-col h-full overflow-y-auto max-h-[80vh]">
              <FormRow cols="sm:2" gap={2} className="flex-col sm:flex-row gap-2 sm:gap-4">
                <div className="relative w-full mt-2">
                  <CustomDropdown
                    value={projectsList.find(
                      (project) => project.id === values?.projectId
                    )}
                    name="projectId"
                    customClasses="w-full text-left text-xs sm:text-sm"
                    handleChange={(selectedOption) => {
                      setFieldValue('projectId', selectedOption.id);
                    }}
                    placeholder="Select Project"
                    options={projectsList}
                    handleGetOptionValue={(option) => option.id}
                    handleGetOptionName={(option) => option.projectName}
                    isDisabled={true}
                    required
                  />
                  {errors.projectId && touched.projectId && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                      {errors.projectId}
                    </div>
                  )}
                </div>
                <div className="relative w-full mt-2">
                  <CustomDropdown
                    value={[
                      { value: 'Office', label: 'Office' },
                      { value: 'Remote', label: 'Remote' },
                    ].find(
                      (work_location) => work_location.value === values?.work_location
                    )}
                    name="work_location"
                    customClasses="w-full text-left text-xs sm:text-sm"
                    handleChange={(selectedOption) => {
                      setFieldValue('work_location', selectedOption?.value);
                    }}
                    placeholder="Select work location"
                    options={[
                      { value: 'Office', label: 'Office' },
                      { value: 'Remote', label: 'Remote' },
                    ]}
                    handleGetOptionValue={(option) => option.value}
                    handleGetOptionName={(option) => option.label}
                    required
                  />
                  {errors.work_location && touched.work_location && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                      {errors.work_location}
                    </div>
                  )}
                </div>
              </FormRow>

              <FormRow className="mt-4 flex-col sm:flex-row gap-2 sm:gap-4">
                <div className="w-full relative">
                  <MuiDatePicker
                    label="Start Date"
                    value={values?.startDate}
                    onChange={(date) => {
                      if (date) {
                        setFieldValue('startDate', date);
                        if (values?.endDate && moment(date, "MM-DD-YYYY").isAfter(moment(values.endDate, "MM-DD-YYYY"))) {
                          setFieldValue('endDate', null);
                        }
                      }
                    }}
                    required
                    minDate={values?.projectId ? moment(projectsList.find(project => project.id === values.projectId)?.startDate, "MM-DD-YYYY") : null}
                    maxDate={values?.projectId ? moment(projectsList.find(project => project.id === values.projectId)?.endDate, "MM-DD-YYYY") : moment().endOf("day")}
                    className="text-xs sm:text-sm"
                  />
                  {errors.startDate && touched.startDate && (
                    <p className="mt-1 text-xs sm:text-sm text-rose-500 text-left">
                      {errors.startDate}
                    </p>
                  )}
                </div>

                <div className="w-full relative">
                  <MuiDatePicker
                    label="End Date"
                    value={values?.endDate}
                    onChange={(date) => {
                      if (date) {
                        const selectedProject = projectsList.find(
                          (project) => project.id === values?.projectId
                        );
                        if (selectedProject && selectedProject.endDate && moment(date, "MM-DD-YYYY").isAfter(moment(selectedProject.endDate, "MM-DD-YYYY"))) {
                          toast.error("End date exceeds project end date");
                          return;
                        }
                        setFieldValue('endDate', date);
                      }
                    }}
                      minDate={values?.startDate ? moment(values.startDate, "MM-DD-YYYY") : null}
                    maxDate={values?.projectId ? moment(projectsList.find(project => project.id === values.projectId)?.endDate, "MM-DD-YYYY") : moment().endOf("day")}
                    className="text-xs sm:text-sm"
                    required
                  />
                  {errors.endDate && touched.endDate && (
                    <p className="mt-1 text-xs sm:text-sm text-rose-500 text-left">
                      {errors.endDate}
                    </p>
                  )}
                </div>
                <div className="w-full relative">
                  <FormField
                    type="number"
                    name="total_hours"
                    value={values?.total_hours}
                    onChange={handleChange}
                    error={errors.total_hours}
                    touched={touched.total_hours}
                    placeholder="Enter total hours"
                    required
                    legend
                    className="w-full text-xs sm:text-sm"
                  />
                  {errors.total_hours && touched.total_hours && (
                    <div className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                      {errors.total_hours}
                    </div>
                  )}
                </div>
                 {/* Rejection Reason Display */}
               {(timesheetValues?.status === 'RESUB' || timesheetValues?.status === 'REJ') && timesheetValues?.remarks && (
                
                  <div className="w-full relative">
                    <FormField
                      type="text"
                      name="rejection_reason"
                      value={timesheetValues.remarks}
                      onChange={() => {}} // Read-only, no change handler
                      placeholder="Rejection Reason"
                      disabled={true}
                      legend
                      className="w-full text-xs sm:text-sm bg-gray-50"
                    />
                  </div>
                
              )}
              </FormRow>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700 text-sm sm:text-base">
                    Holidays
                  </h3>
                  <FormButton
                    type="button"
                    size="sm"
                    onClick={() => {
                      const newHoliday = {
                        employee_holiday_date: null,
                        notes: ""
                      };
                      setFieldValue('holidays', [...values.holidays, newHoliday]);
                    }}
                    icon={<FaPlus />}
                    className="bg-primary hover:bg-blue-700 text-white transition-colors px-3 py-1 text-xs sm:text-sm"
                  >
                    Add Holiday
                  </FormButton>
                </div>

                <div className="space-y-3">
                  {values.holidays.length > 0 ? (
                    values.holidays.map((holiday, index) => (
                      <div
                        key={index}
                        className="p-3 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
                      >
                        <FormRow cols="1 sm:2" gap={2} className="flex-col sm:flex-row gap-2 sm:gap-4">
                          <div className="w-full relative">
                            <MuiDatePicker
                              label="Holiday Date"
                              value={holiday.employee_holiday_date}
                              onChange={(date) => {
                                if (date) {
                                  const updatedHolidays = [...values.holidays];
                                  updatedHolidays[index] = {
                                    ...updatedHolidays[index],
                                    employee_holiday_date: date
                                  };
                                  setFieldValue('holidays', updatedHolidays);
                                }
                              }}
                              minDate={values?.startDate ? moment(values.startDate, "MM-DD-YYYY") : null}
                              maxDate={values?.endDate ? moment(values.endDate, "MM-DD-YYYY") : null}
                              className="text-xs sm:text-sm"
                            />
                          </div>

                          <div className="w-full relative">
                            <FormField
                              type="text"
                              name={`holidays[${index}].notes`}
                              value={holiday.notes}
                              onChange={(e) => {
                                const updatedHolidays = [...values.holidays];
                                updatedHolidays[index] = {
                                  ...updatedHolidays[index],
                                  notes: e.target.value
                                };
                                setFieldValue('holidays', updatedHolidays);
                              }}
                              placeholder="Enter holiday notes"
                              legend
                              className="w-full text-xs sm:text-sm"
                            />
                            <button
                              type="button"
                              className="absolute top-4 right-2 text-rose-500 hover:text-rose-700 transition-colors"
                              onClick={() => {
                                const updatedHolidays = values.holidays.filter((_, i) => i !== index);
                                setFieldValue('holidays', updatedHolidays);
                              }}
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </FormRow>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex items-center justify-center">
                      <p className="text-gray-500 text-xs sm:text-sm">
                        Click the "Add Holiday" button to add holidays
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700 text-sm sm:text-base">
                    Attachments
                  </h3>
                </div>

                {/* Display existing files */}
                {values.files && values.files.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Existing Files:</h4>
                    <div className="space-y-2">
                      {values.files.map((file, index) => (
                        <div
                          key={file.id || index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              <svg className="w-5 h-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2-2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate text-left">
                                {file.fileName || file.name}
                              </p>
                              {/* <p className="text-xs text-gray-500">
                                {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                              </p> */}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={(e) => handleDownloadFile(e, file.id)}
                              disabled={downloadFileState?.loading}
                              className={`text-indigo-500 hover:text-indigo-700 transition-colors p-1 rounded ${
                                downloadFileState?.loading ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title={downloadFileState?.loading ? "Downloading..." : "Download file"}
                            >
                              <FaDownload className={`w-4 h-4 ${downloadFileState?.loading ? 'animate-pulse' : ''}`} />
                            </button>
                            <button
                              onClick={(e) => handleDeleteExistingFile(e, file.id, setFieldValue, values)}
                              className="text-rose-500 hover:text-rose-700 transition-colors p-1 rounded"
                              title="Delete file"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Display newly selected files */}
                {selectedFiles.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">New Files:</h4>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={`new-${index}`}
                          className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              <svg className="w-5 h-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={(e) => handleDeleteFile(e, index, setFieldValue)}
                              className="text-rose-500 hover:text-rose-700 transition-colors p-1 rounded"
                              title="Remove file"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File upload area */}
                <div
                  className={`flex items-center justify-center w-full py-2 ${
                    isDragging ? 'bg-indigo-50' : ''
                  }`}
                  onDrop={(e) => handleDrop(e, setFieldValue)}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <label
                    htmlFor="dropzone-file"
                    className="flex flex-col items-center justify-center w-full h-16 sm:h-20 border-2 border-dashed rounded-lg cursor-pointer border-indigo-500/30 bg-indigo-50/30 hover:bg-indigo-50 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-2 pb-2">
                      <svg
                        className="w-5 h-5 sm:w-6 sm:h-6 mb-1 text-indigo-500"
                        aria-hidden="true"
                        fill="none"
                        viewBox="0 0 20 16"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                        />
                      </svg>
                      <p className="mb-0 text-xs sm:text-sm text-gray-700 font-medium">
                        {!values.files?.length && !selectedFiles.length ? (
                          <span>Click to upload or drag and drop</span>
                        ) : (
                          <span>Add more files</span>
                        )}
                      </p>
                    </div>
                    <input
                      id="dropzone-file"
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileChange}
                      multiple
                    />
                  </label>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white p-4 sm:p-0 mt-4 pb-4 sm:pb-0 flex justify-end">
                <FormButton
                  type="submit"
                  disabled={isLoading || isSubmitting}
                  className={`px-4 py-2 h-10 w-full sm:w-40 rounded-lg text-white transition text-xs sm:text-sm
                    ${
                      isLoading || isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-indigo-500 hover:bg-indigo-700 cursor-pointer'
                    }`}
                >
                  {isLoading || isSubmitting ? 'Updating...' : 'Update'}
                </FormButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </ModalComponent>
  );
};

export default EditTimesheet;
